import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { MainLayout } from "@/layouts/MainLayout";
import { DataTable, FilterPanel } from "@/components/common";
import type { Column, FilterConfig } from "@/components/common";
import { ingressSourceLabel } from "@/utils/ingressSource";
import { productClicksApi, utmOptionsApi } from "@/api";
import { productsApi } from "@/api/products.api";
import type { UtmOptions } from "@/types/utmOptions.types";
import { CreateUtmLinkModal } from "@/components/CreateUtmLinkModal";
import type { ProductClick } from "@/types/productClicks.types";
import { exportToExcel, type ExportColumn } from "@/utils/exportToExcel";
import { PlusIcon, ExcelIcon } from "@/components/icons";
import styles from "./Leads.module.css";

const SEARCH_TYPE_OPTIONS = [
  { label: "Tracking ID", value: "tracking_id" },
  { label: "Token", value: "token" },
  { label: "Product ID", value: "product_id" },
  { label: "Agent Name", value: "agent_name" },
  { label: "Mobile", value: "mob_num" },
  { label: "Sub", value: "sub" },
];

const STATIC_COLUMNS: Column<ProductClick>[] = [
  {
    key: "tracking_id",
    label: "Tracking ID",
    sortable: true,
    defaultVisible: true,
  },
  {
    key: "created_at",
    label: "Clicked At",
    sortable: true,
    defaultVisible: true,
    render: (value) => {
      try {
        if (!value) return "-";
        const date = new Date(value as string);
        date.setMinutes(date.getMinutes() - 330);
        return date.toLocaleString();
      } catch {
        return String(value ?? "-");
      }
    },
  },
  {
    key: "customer_name",
    label: "Name",
    sortable: false,
    defaultVisible: true,
    // resolved from users_ when the click has a known user_id (portal apply)
    render: (value) => String(value || "-"),
  },
  {
    key: "mob_num",
    label: "Mobile",
    sortable: false,
    defaultVisible: true,
    // prefer users_-resolved contact, fall back to the click's own value
    render: (value, row) => String(row.customer_mob_num || value || "-"),
  },
  {
    key: "product_id",
    label: "Product",
    sortable: true,
    defaultVisible: true,
  },
  {
    key: "agent_name",
    label: "Agent",
    sortable: true,
    defaultVisible: true,
    render: (value, row) => String(value || row.agent_id || "-"),
  },
  {
    key: "source",
    label: "Source",
    sortable: true,
    defaultVisible: true,
    render: (value) => ingressSourceLabel(value),
  },
  {
    key: "campaign_id",
    label: "Campaign",
    sortable: true,
    defaultVisible: true,
  },
  {
    key: "user_id",
    label: "User ID",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "sub",
    label: "Sub",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "os",
    label: "OS",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "browser",
    label: "Browser",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "device",
    label: "Device",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "is_bot",
    label: "Bot",
    sortable: true,
    defaultVisible: true,
    render: (value) => (value ? "Yes" : "No"),
  },
  {
    key: "customer_email",
    label: "Email",
    sortable: false,
    defaultVisible: false,
    render: (value, row) => String(value || row.email || "-"),
  },
  {
    key: "utm_source",
    label: "UTM Source",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "utm_medium",
    label: "UTM Medium",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "utm_term",
    label: "UTM Term",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "utm_content",
    label: "UTM Content",
    sortable: true,
    defaultVisible: true,
    render: (value) => String(value || "-"),
  },
  {
    key: "ip",
    label: "IP",
    sortable: false,
    defaultVisible: false,
  },
  {
    key: "token",
    label: "Token",
    sortable: true,
    defaultVisible: false,
  },
  {
    key: "redirect_link",
    label: "Redirect",
    sortable: false,
    defaultVisible: false,
    render: (value) => {
      const v = String(value || "");
      if (!v) return "-";
      return (
        <span title={v} style={{ maxWidth: 180, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {v}
        </span>
      );
    },
  },
];

const FILTER_CONFIGS_BASE: FilterConfig[] = [
  {
    key: "search_type",
    label: "Search Type",
    type: "select",
    options: SEARCH_TYPE_OPTIONS,
  },
  {
    key: "search_text",
    label: "Search",
    type: "text",
    placeholder: "Enter search text...",
  },
  {
    key: "product_id",
    label: "Product",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search products...",
  },
  {
    key: "utm_source",
    label: "UTM Source",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search source...",
  },
  {
    key: "utm_medium",
    label: "UTM Medium",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search medium...",
  },
  {
    key: "utm_term",
    label: "UTM Term",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search term...",
  },
  {
    key: "utm_content",
    label: "UTM Content",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search content...",
  },
  {
    key: "campaign_id",
    label: "Campaign",
    type: "searchableMultiSelect",
    options: [],
    placeholder: "Search campaign...",
  },
  {
    key: "date_range",
    label: "Date Range",
    type: "dateRangeField",
    dateRangeFields: [{ label: "Clicked At", value: "created_at" }],
  },
];

// Distinct-value dropdown filters; options come from the utm-options API.
const OPTION_FILTER_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_term",
  "utm_content",
  "campaign_id",
] as const;

export const ProductClicks: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [clicks, setClicks] = useState<ProductClick[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;

  // Campaign filter/column are admin-only (selfOnboard).
  const isSelfOnboard =
    (localStorage.getItem("agent_id") || localStorage.getItem("agentId")) ===
    "selfOnboard";

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState<number | undefined>(undefined);
  const [exportLimit, setExportLimit] = useState(10);
  const [exporting, setExporting] = useState(false);
  const [showUtmModal, setShowUtmModal] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>("desc");

  const [filters, setFilters] = useState<Record<string, unknown>>(() => {
    const product_id = searchParams.getAll("product_id");
    const date_range = searchParams.getAll("date_range");
    const utm: Record<string, string[]> = {};
    OPTION_FILTER_KEYS.forEach((k) => {
      const v = searchParams.getAll(k);
      utm[k] = v.length > 0 ? v : [];
    });
    return {
      product_id: product_id.length > 0 ? product_id : [],
      date_range_field: searchParams.get("date_range_field") || "created_at",
      date_range: date_range.length === 2 ? date_range : null,
      search_text: searchParams.get("search_text") || "",
      search_type: searchParams.get("search_type") || null,
      ...utm,
    };
  });

  const [searchTextInput, setSearchTextInput] = useState(
    () => searchParams.get("search_text") || ""
  );

  const [productFilterOptions, setProductFilterOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const [utmOptions, setUtmOptions] = useState<UtmOptions>({
    utm_source: [],
    utm_medium: [],
    utm_term: [],
    utm_content: [],
    campaign_id: [],
    sub: [],
  });

  const FILTER_CONFIGS: FilterConfig[] = useMemo(
    () =>
      FILTER_CONFIGS_BASE.filter(
        (f) => f.key !== "campaign_id" || isSelfOnboard
      ).map((f) => {
        if (f.key === "product_id") {
          return { ...f, options: productFilterOptions };
        }
        if ((OPTION_FILTER_KEYS as readonly string[]).includes(f.key)) {
          const vals = utmOptions[f.key as keyof UtmOptions] || [];
          return { ...f, options: vals.map((v) => ({ label: v, value: v })) };
        }
        return f;
      }),
    [productFilterOptions, utmOptions, isSelfOnboard]
  );

  const columns = useMemo(
    () =>
      isSelfOnboard
        ? STATIC_COLUMNS
        : STATIC_COLUMNS.filter((c) => c.key !== "campaign_id"),
    [isSelfOnboard]
  );

  useEffect(() => {
    if (!isAuthenticated) navigate("/agent-login");
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    productsApi.getProducts({ channel: "AGENT" }).then((result) => {
      if (result.status === "Success" && Array.isArray(result.response)) {
        setProductFilterOptions(
          result.response.map((p) => ({
            label: `${p.name} (${p.code || p.id})`,
            value: p.id,
          }))
        );
      }
    });
  }, []);

  useEffect(() => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");
    if (!agentId) return;
    utmOptionsApi
      .getUtmOptions({ agent_id: agentId, scope: "clicks" })
      .then(setUtmOptions);
  }, [agentIdOverride]);

  const buildBaseRequest = useCallback(
    (agentId: string, currentPage: number, currentPageSize: number) => {
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
              filters.date_range[0] as string | null,
              filters.date_range[1] as string | null,
            ]
          : [null, null];

      const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
        ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
        : { created_at: -1 };

      const utmFilters: Record<string, string[]> = {};
      OPTION_FILTER_KEYS.forEach((k) => {
        const v = filters[k];
        if (Array.isArray(v) && v.length > 0) utmFilters[k] = v as string[];
      });

      return {
        agent_id: agentId,
        page: currentPage,
        page_size: currentPageSize,
        ranges: { created_at: dateRange },
        sort_by: sortByValue,
        filters: {
          product_id:
            Array.isArray(filters.product_id) && filters.product_id.length > 0
              ? (filters.product_id as string[])
              : undefined,
          ...utmFilters,
        },
      };
    },
    [filters, sortBy, sortOrder]
  );

  const fetchClicks = useCallback(async () => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");

    if (!agentId) {
      setError("Agent ID not found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string | null;
      const request = {
        ...buildBaseRequest(agentId, page, pageSize),
        search_text: searchText || undefined,
        search_type: searchType
          ? (searchType as GetProductClicksSearchType)
          : undefined,
      };

      const response = await productClicksApi.getProductClicks(request);

      if (response.response) {
        setClicks(response.response);
        setTotal(response.pagination_metadata?.total_records);
      } else {
        setError(response.message || "Failed to fetch product clicks");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch product clicks"
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, buildBaseRequest, filters, agentIdOverride]);

  useEffect(() => {
    if (isAuthenticated) fetchClicks();
  }, [isAuthenticated, fetchClicks]);

  const handleSort = (key: string, order: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === "search_text") {
      const text = typeof value === "string" ? value : "";
      setSearchTextInput(text);
      if (!text) {
        setFilters((prev) => ({ ...prev, search_text: "" }));
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.delete("search_text");
          return p;
        });
      }
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (Array.isArray(value)) {
        p.delete(key);
        value.forEach((v) => p.append(key, String(v)));
      } else if (value !== null && value !== undefined && value !== "") {
        p.set(key, String(value));
      } else {
        p.delete(key);
      }
      return p;
    });
  };

  const handleSearchSubmit = (_key: string, value: unknown) => {
    const text = typeof value === "string" ? value : "";
    setFilters((prev) => ({ ...prev, search_text: text }));
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (text) p.set("search_text", text);
      else p.delete("search_text");
      return p;
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchTextInput("");
    setFilters({
      product_id: [],
      date_range_field: "created_at",
      date_range: null,
      search_text: "",
      search_type: null,
      utm_source: [],
      utm_medium: [],
      utm_term: [],
      utm_content: [],
      campaign_id: [],
    });
    setPage(1);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      [
        "product_id",
        "date_range_field",
        "date_range",
        "search_text",
        "search_type",
        ...OPTION_FILTER_KEYS,
      ].forEach((k) => p.delete(k));
      return p;
    });
  };

  const handleExportToExcel = async () => {
    setExporting(true);
    setError("");
    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");
      if (!agentId) throw new Error("Agent ID not found");

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string | null;
      const response = await productClicksApi.getProductClicks({
        ...buildBaseRequest(agentId, 1, exportLimit),
        search_text: searchText || undefined,
        search_type: searchType
          ? (searchType as GetProductClicksSearchType)
          : undefined,
      });

      const dataToExport = response.response || [];
      if (dataToExport.length === 0) {
        alert("No data found to export.");
        return;
      }

      const exportColumns: ExportColumn<ProductClick>[] = columns.map(
        (col) => ({
          key: col.key,
          label: col.label,
          render: (value: unknown, row: ProductClick) => {
            if (col.key === "is_bot") return row.is_bot ? "Yes" : "No";
            if (col.key === "created_at" && value) {
              try {
                const date = new Date(value as string);
                date.setMinutes(date.getMinutes() - 330);
                return date.toLocaleString();
              } catch {
                return String(value);
              }
            }
            if (value === null || value === undefined || value === "") return "-";
            return String(value);
          },
        })
      );

      exportToExcel(
        dataToExport as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        "product_clicks"
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to export data";
      setError(msg);
      alert(`Export Failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Product Clicks
              {agentName && (
                <span className={styles.agentContext}>({agentName})</span>
              )}
            </h1>
            {agentName && (
              <p className={styles.agentContextInfo}>
                Viewing clicks for: {agentName} (ID: {agentIdOverride})
              </p>
            )}
          </div>

          <div className={styles.headerControls}>
            <button
              className={styles.primaryToolbarButton}
              onClick={() => setShowUtmModal(true)}
              title="Create a distributable UTM link"
            >
              <PlusIcon size={18} />
              Create UTM Link
            </button>
            <div className={styles.exportToolbar}>
              <select
                value={exportLimit}
                onChange={(e) => setExportLimit(Number(e.target.value))}
                className={styles.limitSelect}
              >
                <option value={10}>Export 10</option>
                <option value={50}>Export 50</option>
                <option value={100}>Export 100</option>
                <option value={500}>Export 500</option>
                <option value={1000}>Export 1000</option>
              </select>
              <button
                className={styles.exportButton}
                onClick={() => handleExportToExcel()}
                disabled={loading || exporting || clicks.length === 0}
              >
                {exporting ? (
                  "Exporting..."
                ) : (
                  <>
                    <ExcelIcon size={18} />
                    Export to Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <FilterPanel
          filters={FILTER_CONFIGS}
          values={{ ...filters, search_text: searchTextInput }}
          onChange={handleFilterChange}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleResetFilters}
        />

        <DataTable
          data={clicks}
          columns={columns}
          loading={loading}
          tableId="product_clicks"
          pagination={{
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
          }}
          sorting={{ sortBy, sortOrder, onSort: handleSort }}
          onRowActions={[
            {
              label: "Resume Journey",
              variant: "primary",
              onClick: (row) => {
                if (row.redirect_link) {
                  const w = window.open(row.redirect_link, "_blank");
                  if (w) w.opener = null;
                }
              },
            },
          ]}
          emptyMessage="No product clicks found"
        />
      </div>

      {showUtmModal && (
        <CreateUtmLinkModal
          agentId={
            agentIdOverride ||
            localStorage.getItem("agent_id") ||
            localStorage.getItem("agentId") ||
            ""
          }
          onClose={() => setShowUtmModal(false)}
        />
      )}
    </MainLayout>
  );
};

type GetProductClicksSearchType =
  | "tracking_id"
  | "token"
  | "product_id"
  | "agent_name"
  | "mob_num"
  | "sub";
